import React from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import AppShell from "./layouts/AppShell";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Pricing from "./pages/Pricing";
import JobsDashboard from "./pages/jobs/JobsDashboard";
import BuildWizard from "./pages/build/BuildWizard";
import ReviewWizard from "./pages/review/ReviewWizard";
import BillingSettings from "./pages/settings/BillingSettings";
import DevUsersSettings from "./pages/settings/DevUsersSettings";
import CrmWorkspace from "./pages/crm/CrmWorkspace";
import InspectionCalendar from "./pages/inspections/InspectionCalendar";
import InspectionChecklist from "./pages/inspections/InspectionChecklist";
import InvoiceStudio from "./pages/invoices/InvoiceStudio";
import InsuranceCompaniesDirectory from "./pages/insurance/InsuranceCompaniesDirectory";
import MitigationStation from "./pages/mitigation/MitigationStation";
import PersonalPropertyWorkspace from "./pages/personalProperty/PersonalPropertyWorkspace";
import GrowthConsole from "./pages/growth/GrowthConsole";
import RepairEstimate from "./pages/repairEstimate/RepairEstimate";
import RepairEstimateReport from "./pages/repairEstimate/RepairEstimateReport";
import { apiFetch, getStoredToken } from "./api/client";

function RequireAuth({ children }) {
  const location = useLocation();
  const token = getStoredToken();
  if (!token) {
    const next = `${location.pathname}${location.search || ""}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return children;
}

function RequireDevAdmin({ children }) {
  const [loading, setLoading] = React.useState(true);
  const [allowed, setAllowed] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await apiFetch("/auth/dev/users", { method: "GET" });
        if (mounted) setAllowed(true);
      } catch (_e) {
        if (mounted) setAllowed(false);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return <div className="p-6 text-sm text-slate-600">Checking dev access...</div>;
  }
  if (!allowed) {
    return <Navigate to="/app/settings/billing" replace />;
  }
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/repair-estimate" element={<RepairEstimate />} />
        <Route path="/repair-estimate/report" element={<RequireAuth><RepairEstimateReport /></RequireAuth>} />
        <Route
          path="/mitigation"
          element={(
            <RequireAuth>
              <Navigate to="/app/mitigation" replace />
            </RequireAuth>
          )}
        />

        <Route
          path="/app"
          element={(
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          )}
        >
          <Route index element={<Navigate to="build" replace />} />
          <Route path="build" element={<BuildWizard />} />
          <Route path="jobs" element={<JobsDashboard />} />
          <Route path="crm" element={<CrmWorkspace />} />
          <Route path="inspections" element={<InspectionCalendar />} />
          <Route path="inspection-checklist" element={<InspectionChecklist />} />
          <Route path="invoices" element={<InvoiceStudio />} />
          <Route path="guidelines" element={<Navigate to="/app/build" replace />} />
          <Route path="insurance-companies" element={<InsuranceCompaniesDirectory />} />
          <Route path="mitigation" element={<MitigationStation />} />
          <Route path="personal-property" element={<PersonalPropertyWorkspace />} />
          <Route path="review" element={<ReviewWizard />} />
          <Route
            path="growth"
            element={(
              <RequireDevAdmin>
                <GrowthConsole />
              </RequireDevAdmin>
            )}
          />
          <Route path="settings/billing" element={<BillingSettings />} />
          <Route
            path="settings/dev-users"
            element={(
              <RequireDevAdmin>
                <DevUsersSettings />
              </RequireDevAdmin>
            )}
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
